import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { 
  Box, Stack, Group, Title, Button, Card, Text, Badge, Modal, 
  TextInput, Textarea, Select, ActionIcon, Grid, Paper, ThemeIcon, 
  Divider, SimpleGrid, Loader, Alert
} from '@mantine/core';
import { 
  IconMapPin, IconBrandGoogle, IconPlus, IconEdit, IconTrash, 
  IconRocket, IconCheck, IconBuildingStore
} from '@tabler/icons-react';

export default function GoogleMapsTab({ user, apiBaseUrl }) {
  const { t } = useTranslation();
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(false);
  
  const [modalOpened, setModalOpened] = useState(false);
  const [editingId, setEditingId] = useState(null);
  
  const [form, setForm] = useState({ 
    businessName: '', 
    category: '', 
    address: '', 
    phone: '', 
    website: '', 
    description: '',
    status: 'DRAFT'
  });

  const fetchProfiles = async () => {
    try {
      setLoading(true);
      const res = await fetch(`${apiBaseUrl}/api/google-business?ownerEmail=${encodeURIComponent(user?.email || 'admin@ailinkb.com')}`);
      if (res.ok) {
        const data = await res.json();
        setProfiles(data);
      }
    } catch (e) {
      console.error("Błąd pobierania wizytówek:", e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProfiles();
    const interval = setInterval(() => {
      fetchProfiles();
    }, 5000); // Poll every 5s for status updates
    return () => clearInterval(interval);
  }, [user, apiBaseUrl]);

  const handleSave = async () => {
    if (!form.businessName || !form.address) return;
    
    const payload = {
      ...form,
      ownerEmail: user?.email || 'admin@ailinkb.com'
    };

    try {
      const url = editingId 
        ? `${apiBaseUrl}/api/google-business/${editingId}`
        : `${apiBaseUrl}/api/google-business`;
      
      const method = editingId ? 'PUT' : 'POST';

      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      if (res.ok) {
        setModalOpened(false);
        resetForm();
        fetchProfiles();
      }
    } catch (e) {
      console.error("Błąd zapisu wizytówki:", e);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Czy na pewno chcesz usunąć tę wizytówkę?")) return;
    try {
      await fetch(`${apiBaseUrl}/api/google-business/${id}`, { method: 'DELETE' });
      setProfiles(prev => prev.filter(p => p.id !== id));
    } catch (e) { 
      console.error(e); 
    }
  };

  const handlePublish = async (id) => {
    try {
      const email = encodeURIComponent(user?.email || 'admin@ailinkb.com');
      const res = await fetch(`${apiBaseUrl}/api/google/oauth/url?email=${email}&profileId=${id}`);
      if (res.ok) {
        const data = await res.json();
        // Redirect user to Google OAuth consent screen
        window.location.href = data.url;
      } else {
        alert(t('oauth_error', 'Błąd generowania linku autoryzacji Google.'));
      }
    } catch (e) {
      console.error(e);
      alert(t('oauth_error', 'Błąd generowania linku autoryzacji Google.'));
    }
  };

  const resetForm = () => {
    setEditingId(null);
    setForm({ 
      businessName: '', 
      category: '', 
      address: '', 
      phone: '', 
      website: '', 
      description: '',
      status: 'DRAFT'
    });
  };

  const openEditModal = (p) => {
    setEditingId(p.id);
    setForm({
      businessName: p.businessName || '',
      category: p.category || '',
      address: p.address || '',
      phone: p.phone || '',
      website: p.website || '',
      description: p.description || '',
      status: p.status || 'DRAFT'
    });
    setModalOpened(true);
  };

  const [oauthStatus, setOauthStatus] = useState(null);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('oauth') === 'success') {
      setOauthStatus('success');
      // Clean up URL
      window.history.replaceState({}, '', '/dashboard?tab=google-maps');
    } else if (params.get('oauth') === 'error') {
      setOauthStatus('error');
      window.history.replaceState({}, '', '/dashboard?tab=google-maps');
    }
  }, []);

  return (
    <Box p="md" style={{ background: '#f8fafc', minHeight: 'calc(100vh - 120px)', borderRadius: 12 }}>
      
      {oauthStatus === 'success' && (
        <Alert icon={<IconCheck size={16} />} title={t('oauth_success_title', 'Autoryzacja udana!')} color="green" mb="md" withCloseButton onClose={() => setOauthStatus(null)}>
          {t('oauth_success_desc', 'Konto Google zostało poprawnie połączone. Trwa publikacja do Google Maps w tle (zakolejkowano).')}
        </Alert>
      )}
      
      {oauthStatus === 'error' && (
        <Alert icon={<IconRocket size={16} />} title={t('oauth_error_title', 'Błąd autoryzacji')} color="red" mb="md" withCloseButton onClose={() => setOauthStatus(null)}>
          {t('oauth_error_desc', 'Nie udało się połączyć z kontem Google lub autoryzacja została odrzucona.')}
        </Alert>
      )}

      {/* NAGŁÓWEK */}
      <Paper p="xl" radius="md" mb="xl" style={{ 
        background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #334155 100%)', 
        color: 'white',
        boxShadow: '0 10px 25px -5px rgba(15, 23, 42, 0.4)' 
      }}>
        <Grid align="center">
          <Grid.Col span={{ base: 12, md: 8 }}>
            <Group gap="md">
              <ThemeIcon size={48} radius="md" variant="white" color="red">
                <IconMapPin size={28} />
              </ThemeIcon>
              <Box>
                <Title order={2} style={{ color: 'white', fontWeight: 700 }}>
                  {t('google_maps_title', 'Wizytówki Google Maps')}
                </Title>
                <Text size="sm" c="gray.3" mt={4}>
                  {t('google_maps_desc', 'Zarządzaj widocznością swojej firmy na mapach Google. Dodawaj informacje o lokalizacji, kontakt i integruj to z asystentem AI.')}
                </Text>
              </Box>
            </Group>
          </Grid.Col>
          <Grid.Col span={{ base: 12, md: 4 }} style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button 
              size="md" 
              radius="md" 
              variant="gradient" 
              gradient={{ from: 'red', to: 'orange' }} 
              leftSection={<IconPlus size={18} />}
              onClick={() => { resetForm(); setModalOpened(true); }}
              style={{ boxShadow: '0 4px 12px rgba(239, 68, 68, 0.3)' }}
            >
              {t('btn_new_profile', 'Nowa Wizytówka')}
            </Button>
          </Grid.Col>
        </Grid>
      </Paper>

      {/* SIATKA WIZYTÓWEK */}
      <SimpleGrid cols={{ base: 1, md: 2, lg: 3 }} spacing="lg">
        {profiles.map((p) => {
          const isPublished = p.status === 'PUBLISHED';
          
          return (
            <Card 
              key={p.id} 
              shadow="sm" 
              radius="md" 
              withBorder 
              p="lg" 
              style={{ 
                display: 'flex', 
                flexDirection: 'column', 
                justifyContent: 'space-between',
                transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                '&:hover': { transform: 'translateY(-4px)', boxShadow: '0 12px 24px -10px rgba(0, 0, 0, 0.15)' },
                background: 'white',
                borderTop: '4px solid #ef4444'
              }}
            >
              <Box>
                <Group justify="space-between" mb="xs">
                  <Badge 
                    variant="filled" color="red" 
                    leftSection={<IconBrandGoogle size={14} />}
                    size="md"
                  >
                    Google Maps
                  </Badge>
                  <Badge 
                    variant={['PUBLISHED', 'PENDING_VERIFICATION'].includes(p.status) ? 'filled' : 'outline'} 
                    color={
                      p.status === 'PUBLISHED' ? 'green' : 
                      p.status === 'PENDING_VERIFICATION' ? 'orange' :
                      p.status === 'PUBLISHING' ? 'blue' :
                      p.status === 'PENDING_OAUTH' ? 'violet' : 'yellow'
                    }
                  >
                    {p.status || 'DRAFT'}
                  </Badge>
                </Group>

                <Group gap="xs" mb="xs">
                  <IconBuildingStore size={20} color="#334155" />
                  <Title order={4} style={{ color: '#1e293b', fontWeight: 600 }}>
                    {p.businessName}
                  </Title>
                </Group>

                <Box mb="md" p="sm" style={{ background: '#f1f5f9', borderRadius: 8 }}>
                  <Text size="xs" c="dimmed" fw={700} tt="uppercase" mb={4}>{t('company_address', 'Adres Firmy')}</Text>
                  <Text size="sm" fw={600} c="dark.8">📍 {p.address}</Text>
                </Box>

                <Text size="sm" c="gray.7" mb="sm" lineClamp={2}>
                  {p.description || t('no_company_desc', 'Brak opisu firmy.')}
                </Text>

                <Divider my="sm" />

                <Group justify="space-between" mb="sm">
                  <Box>
                    <Text size="xs" c="dimmed" fw={700} tt="uppercase">{t('company_category', 'Kategoria')}</Text>
                    <Text size="sm" fw={500}>{p.category || '-'}</Text>
                  </Box>
                  <Box>
                    <Text size="xs" c="dimmed" fw={700} tt="uppercase" ta="right">{t('company_phone', 'Telefon')}</Text>
                    <Text size="sm" fw={500}>{p.phone || '-'}</Text>
                  </Box>
                </Group>
              </Box>

              <Box mt="md">
                <Group gap="xs" grow>
                  <Button 
                    variant="default" 
                    size="sm" 
                    leftSection={<IconEdit size={16} />}
                    onClick={() => openEditModal(p)}
                  >
                    {t('btn_edit', 'Edytuj')}
                  </Button>
                  <ActionIcon 
                    variant="filled" color="red" 
                    size="lg" 
                    radius="md"
                    onClick={() => handleDelete(p.id)}
                  >
                    <IconTrash size={18} />
                  </ActionIcon>
                  <Button 
                    variant={isPublished ? "light" : "filled"} 
                    color={isPublished ? "gray" : "red"}
                    size="sm" 
                    leftSection={isPublished ? <IconCheck size={16} /> : <IconMapPin size={16} />}
                    onClick={() => handlePublish(p.id)}
                    disabled={['PUBLISHED', 'PUBLISHING', 'PENDING_VERIFICATION', 'PENDING_OAUTH'].includes(p.status)}
                    loading={['PUBLISHING', 'PENDING_OAUTH'].includes(p.status)}
                  >
                    {p.status === 'PENDING_VERIFICATION' ? t('status_pending_verification', 'Oczekuje Weryfikacji') : 
                     p.status === 'PUBLISHED' ? t('status_published', 'Opublikowano') : t('btn_publish_map', 'Publikuj na mapie')}
                  </Button>
                </Group>
              </Box>
            </Card>
          );
        })}
      </SimpleGrid>

      {profiles.length === 0 && !loading && (
        <Paper p="xl" ta="center" radius="md" withBorder mt="xl" style={{ background: 'white' }}>
          <ThemeIcon size={64} radius="full" variant="filled" color="red" mx="auto" mb="md">
            <IconMapPin size={32} />
          </ThemeIcon>
          <Title order={4} c="gray.7" mb="xs">{t('google_maps_empty_title', 'Brak wizytówek Google Maps')}</Title>
          <Text c="dimmed" size="sm" mb="lg" maxW={400} mx="auto">
            {t('google_maps_empty_desc', 'Nie masz jeszcze skonfigurowanej wizytówki firmy. Dodaj ją, aby ułatwić klientom odnalezienie Twojego biznesu i zsynchronizować dane z asystentem AI.')}
          </Text>
          <Button variant="outline" color="red" onClick={() => setModalOpened(true)}>
            {t('btn_add_first_profile', 'Dodaj pierwszą wizytówkę')}
          </Button>
        </Paper>
      )}

      {/* MODAL EDYCJI / TWORZENIA */}
      <Modal 
        opened={modalOpened} 
        onClose={() => setModalOpened(false)} 
        title={<Text fw={700} size="lg">{editingId ? `✏️ ${t('modal_title_edit_profile', 'Edytuj Wizytówkę')}` : `📍 ${t('modal_title_new_profile', 'Nowa Wizytówka Google Maps')}`}</Text>}
        size="lg" 
        radius="md"
      >
        <Stack gap="md">
          <TextInput
            label={t('lbl_business_name', 'Nazwa firmy')}
            placeholder="np. AILinkB - Agencja AI"
            required
            value={form.businessName}
            onChange={(e) => setForm({ ...form, businessName: e.target.value })}
          />

          <TextInput
            label={t('lbl_business_address', 'Pełny adres (ulica, miasto, kod pocztowy)')}
            placeholder="np. ul. Złota 44, 00-120 Warszawa"
            required
            value={form.address}
            onChange={(e) => setForm({ ...form, address: e.target.value })}
          />

          <Grid>
            <Grid.Col span={6}>
              <TextInput
                label={t('lbl_business_category', 'Kategoria działalności')}
                placeholder="np. Usługi IT / Restauracja"
                value={form.category}
                onChange={(e) => setForm({ ...form, category: e.target.value })}
              />
            </Grid.Col>
            <Grid.Col span={6}>
              <TextInput
                label={t('lbl_business_phone', 'Numer telefonu')}
                placeholder="np. +48 123 456 789"
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
              />
            </Grid.Col>
          </Grid>

          <TextInput
            label={t('lbl_business_website', 'Strona WWW')}
            placeholder="np. https://www.ailinkb.com"
            value={form.website}
            onChange={(e) => setForm({ ...form, website: e.target.value })}
          />

          <Textarea
            label={t('lbl_business_desc', 'Opis firmy')}
            description={t('lbl_business_desc_hint', 'Krótki opis Twojej działalności, widoczny w wizytówce Google Maps')}
            placeholder="Zajmujemy się wdrażaniem rozwiązań opartych o sztuczną inteligencję..."
            minRows={3}
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
          />

          <Group justify="flex-end" mt="md">
            <Button variant="default" onClick={() => setModalOpened(false)}>{t('btn_cancel', 'Anuluj')}</Button>
            <Button color="red" onClick={handleSave}>{t('btn_save', 'Zapisz Wizytówkę')}</Button>
          </Group>
        </Stack>
      </Modal>
    </Box>
  );
}
